import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, OnChanges, inject } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { InputValidators } from '../reactive-file-input/validators/nospaces.validator';
import { I18NextEagerPipe } from 'angular-i18next';

@Component({
  selector: 'app-reactive-text-field2',
  templateUrl: './reactive-text-field2.component.html',
  styleUrls: ['./reactive-text-field2.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule],
})
export class ReactiveTextField2Component implements OnInit, OnChanges {
  showPassword: boolean = false;
  @Input() inputType: 'text' | 'number' | 'email' | 'password' = 'text';
  @Input() label: string = '';
  @Input() type: string = 'text';
  @Input() id: string = '';
  @Input() control: FormControl = new FormControl('');
  @Input() placeholder: string = '';
  @Input() required: boolean = false; // Add required attribute
  @Input() errorMessage: string = 'Something is wrong!';
  @Input() hasError: boolean = false;
  @Input() errorType: string = '';
  @Input() socialmedia: string = ''; // Add input for social media type
  @Input() reference: string = '';

  private readonly i18nextPipe = inject(I18NextEagerPipe);

  ValidationErrorMessages: { [key: string]: string } = {
    required: this.i18nextPipe.transform('general_validation.required'),
    email: this.i18nextPipe.transform('general_validation.email'),
    invalidText: this.i18nextPipe.transform('general_validation.invalidText'),
    invalidURL: this.i18nextPipe.transform('general_validation.invalidURL', { url: this.socialmedia }),
    invalidInvoiceId: this.i18nextPipe.transform('general_validation.invalidInvoiceId'),
    lettersOnly: this.i18nextPipe.transform('general_validation.lettersOnly'),
    zipCode: this.i18nextPipe.transform('general_validation.zipCode'),
    other: this.errorMessage,
  };

  constructor() {}

  switchShowPasswordState() {
    this.showPassword = !this.showPassword;

    this.showPassword
      ? (this.inputType = 'text')
      : (this.inputType = 'password');
  }

  ngOnInit() {}

  ngOnChanges(): void {
    // Set up validators based on user input
    const validators = [];
    if (this.required) {
      validators.push(Validators.required);
    }
    if (this.type === 'email') {
      validators.push(Validators.email);
    }
    if (this.type === 'text') {
      validators.push(this.customTextValidator());
    }
    if (this.type === 'url') {
      validators.push(this.urlValidator(this.socialmedia));
    }
    if (this.type === 'invoiceId') {
      validators.push(this.invoiceIdValidator());
    }
    if (this.reference === 'letters-only') {
      validators.push(this.lettersOnlyValidator());
    }
    if (this.reference === 'no-white-space' || this.reference === 'line_1') {
      validators.push(InputValidators.noSpacesValidator());
    }
    if (this.reference === 'zipCode') {
      validators.push(InputValidators.zipCodeValidator());
    }
    this.control.setValidators(validators);
    this.control.updateValueAndValidity();
  }

  public getControlErrorMessage(control: AbstractControl): string | null {
    if (!control || !control.errors || !control.touched) return null;

    const errorKey = Object.keys(control.errors)[0];
    return this.ValidationErrorMessages[errorKey] || 'Invalid input.';
  }

  private customTextValidator() {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      return null;
    };
  }

  private urlValidator(socialmedia: string) {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value?.toLowerCase();
      if (!value) {
        return null;
      }

      const socialMediaPatterns: { [key: string]: RegExp } = {
        facebook: /fb\.com|facebook\.com|fb\.watch/,
        twitter: /twitter\.com|x\.com/,
        tiktok: /tiktok\.com/,
        instagram: /instagram\.com/,
        youtube: /youtube\.com/,
        facebookLive: /fb\.com|facebook\.com|fb\.watch/,
      };

      if (socialmedia && socialMediaPatterns[socialmedia]) {
        if (!socialMediaPatterns[socialmedia].test(value)) {
          return { invalidURL: true };
        }
      } else {
        return { invalidURL: true };
      }

      return null;
    };
  }

  private invoiceIdValidator() {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (!(value && /\d$/.test(value))) {
        return { invalidInvoiceId: true };
      }
      return null;
    };
  }

  private lettersOnlyValidator() {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (value.trim() === '') return { required: true };
      if (value && !/^[\u0E00-\u0E7Fa-zA-Z ]+$/.test(value)) {
        return { lettersOnly: true };
      }
      return null;
    };
  }

  public handleKeyDown(event: KeyboardEvent) {
    const allowedKeys = ['Backspace', 'Delete'];

    const key = event.key;

    if (allowedKeys.includes(key)) return;

    if (this.reference === 'zipCode' && !/^[0-9]$/.test(key)) {
      event.preventDefault();
    }

    if (
      this.reference === 'line_1' &&
      !/^[0-9\u0E00-\u0E7Fa-zA-Z_\/()'#& ., -]*$/.test(key)
    ) {
      event.preventDefault();
    }
  }

  public handleInput(event: Event) {
    const input = event.target as HTMLInputElement;
    let value = input.value;

    if (this.reference === 'zipCode') {
      value = value.replace(/[^0-9]/g, '');
    }

    if (this.reference === 'line_1') {
      value = value.replace(/[^0-9\u0E00-\u0E7Fa-zA-Z_\/()'#& ., -]/g, '');
    }

    input.value = value;
    this.control.setValue(value, { emitEvent: false });
  }
}
